/**
 * A flat, explicit state machine with a data-declared transition table.
 *
 * WHY NOT A BEHAVIOR TREE (see ARCHITECTURE.md): this is the file agents will
 * touch most often, and behavior trees are precisely where coding agents produce
 * confident nonsense - deeply nested composites whose emergent behavior nobody
 * can predict or test. A flat FSM with an explicit table is boring, greppable,
 * and a failing sim test points at exactly one transition.
 *
 * Transitions are evaluated highest-priority-first every step, so an urgent one
 * ("I can see the cat") always wins over a routine one ("finished watering").
 */

export interface State<C> {
  name: string;
  enter?: (ctx: C) => void;
  update?: (ctx: C, dt: number) => void;
  exit?: (ctx: C) => void;
}

export interface Transition<C> {
  /** Source state name, or '*' to apply from any state. */
  from: string | '*';
  to: string;
  when: (ctx: C) => boolean;
  /** Higher wins when several transitions fire on the same step. Default 0. */
  priority?: number;
}

export class StateMachine<C> {
  private states = new Map<string, State<C>>();
  private transitions: Transition<C>[];
  private currentName: string;
  /** Seconds spent in the current state. Available to states via `timeInState`. */
  timeInState = 0;
  /** Set when the state changed this step, for the caller to emit an event. */
  lastChange: { from: string; to: string } | null = null;

  constructor(states: State<C>[], transitions: Transition<C>[], initial: string) {
    for (const state of states) this.states.set(state.name, state);
    if (!this.states.has(initial)) {
      throw new Error(`StateMachine initial state "${initial}" is not registered`);
    }
    // Validate every transition target up front. A typo'd state name would
    // otherwise silently never fire, which is a miserable bug to track down.
    for (const transition of transitions) {
      if (!this.states.has(transition.to)) {
        throw new Error(`Transition targets unknown state "${transition.to}"`);
      }
      if (transition.from !== '*' && !this.states.has(transition.from)) {
        throw new Error(`Transition comes from unknown state "${transition.from}"`);
      }
    }
    // Sort once, descending by priority.
    this.transitions = [...transitions].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    this.currentName = initial;
  }

  get current(): string {
    return this.currentName;
  }

  get currentState(): State<C> {
    const state = this.states.get(this.currentName);
    if (!state) throw new Error(`Current state "${this.currentName}" vanished from the registry`);
    return state;
  }

  /** Fires enter() on the initial state. Call once after construction. */
  start(ctx: C): void {
    this.currentState.enter?.(ctx);
  }

  /**
   * Evaluates transitions, then updates the (possibly new) state.
   * Transitions run BEFORE update so a state never acts on a world it should
   * already have left - that ordering prevents granny spending one extra step
   * watering plants after spotting the cat.
   */
  step(ctx: C, dt: number): void {
    this.lastChange = null;

    for (const transition of this.transitions) {
      if (transition.from !== '*' && transition.from !== this.currentName) continue;
      if (transition.to === this.currentName) continue;
      if (!transition.when(ctx)) continue;
      this.transitionTo(ctx, transition.to);
      break;
    }

    this.timeInState += dt;
    this.currentState.update?.(ctx, dt);
  }

  /** Forces a transition, bypassing the table. Use sparingly. */
  transitionTo(ctx: C, to: string): void {
    if (to === this.currentName) return;
    const next = this.states.get(to);
    if (!next) throw new Error(`Cannot transition to unknown state "${to}"`);

    const from = this.currentName;
    this.currentState.exit?.(ctx);
    this.currentName = to;
    this.timeInState = 0;
    next.enter?.(ctx);
    this.lastChange = { from, to };
  }

  /** Names of every registered state. Used by the debug HUD. */
  get stateNames(): string[] {
    return [...this.states.keys()];
  }
}
